/**
 * sl-9qm.4.2: ArticleList Component
 *
 * Blog-style vertical list. Each entry shows thumbnail left,
 * title + excerpt right, date.
 */
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  BoundDataRow,
  ViewComponent,
  ViewRuntimeContext,
} from '../../../types/view-component.types';
import { ResponsiveImage } from '../../responsive-image/responsive-image';
import * as styles from '../styles.css';
import { getContentCopy } from './content-copy';

const configSchema = z.object({
  showThumbnail: z.boolean().default(true),
  excerptLength: z.number().min(0).max(500).default(160),
  dateFormat: z.string().default('short'),
});

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + '…';
}

function readCell(row: BoundDataRow, keys: readonly string[]): unknown {
  for (const key of keys) {
    const value = row[key];
    if (value !== null && value !== undefined) return value;
  }
  return undefined;
}

function readString(
  row: BoundDataRow,
  keys: readonly string[]
): string | undefined {
  const value = readCell(row, keys);
  if (typeof value === 'string' && value.trim() !== '') return value;
  return undefined;
}

function renderArticle(
  row: BoundDataRow,
  config: z.infer<typeof configSchema>,
  index: number,
  workspaceId?: string
) {
  const title = readString(row, ['title', 'name']) ?? 'Untitled';
  const thumbnail = readCell(row, ['thumbnail', 'image', 'cover']);
  const alt = readString(row, ['alt', 'imageAlt', 'thumbnailAlt']) ?? '';
  const excerpt = readString(row, ['excerpt', 'summary', 'description']);
  const date = readString(row, ['date', 'publishedAt', 'scheduledAt']);

  return createElement(
    'div',
    { key: index, className: styles.articleItem, 'data-sl-role': 'article' },
    // Render the thumbnail block only when the row actually carries image
    // data — an empty placeholder box reads as broken, not as a layout.
    config.showThumbnail &&
      thumbnail != null &&
      thumbnail !== '' &&
      createElement(
        'div',
        {
          className: styles.articleThumbnail,
          'data-sl-field': 'image',
        },
        createElement(ResponsiveImage, {
          image: thumbnail,
          role: 'article',
          alt,
          workspaceId,
          allowWorkspaceBlobFallback: Boolean(workspaceId),
          className: styles.articleThumbnailFill,
        })
      ),
    createElement(
      'div',
      { className: styles.articleBody },
      createElement(
        'div',
        { className: styles.cardTitle, 'data-sl-field': 'title' },
        title
      ),
      excerpt &&
        createElement(
          'div',
          { className: styles.cardExcerpt, 'data-sl-field': 'excerpt' },
          truncate(excerpt, config.excerptLength)
        ),
      date &&
        createElement(
          'div',
          { className: styles.cardMeta, 'data-sl-field': 'date' },
          date
        )
    )
  );
}

export const ArticleListComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.ARTICLE_LIST,
  type: 'content',
  name: 'Article List',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    '☰'
  ),
  capabilities: [
    {
      role: 'primary',
      description: 'Article data rows',
      required: true,
      requirements: [
        { field: 'title', acceptedTypes: ['text'], required: true },
        {
          field: 'thumbnail',
          acceptedTypes: ['attachment', 'url'],
          required: false,
        },
        { field: 'alt', acceptedTypes: ['text'], required: false },
        { field: 'excerpt', acceptedTypes: ['text'], required: false },
        { field: 'date', acceptedTypes: ['date'], required: false },
      ],
    },
  ],
  configSchema,
  defaultConfig: {
    showThumbnail: true,
    excerptLength: 160,
    dateFormat: 'short',
  },
  render({ config, data, dataBindingConfig, mode, runtimeContext }) {
    const typedConfig = config as z.infer<typeof configSchema>;
    const workspaceId = readWorkspaceId(runtimeContext);
    const emptyMessage = dataBindingConfig?.databaseDocId
      ? getContentCopy(runtimeContext?.locale, 'emptyRows')
      : mode === 'builder'
        ? getContentCopy(runtimeContext?.locale, 'notBound')
        : getContentCopy(runtimeContext?.locale, 'unavailable');
    return createElement(
      'div',
      { className: styles.articleList },
      data.length > 0
        ? data.map((row, i) => renderArticle(row, typedConfig, i, workspaceId))
        : createElement(
            'div',
            {
              style: {
                padding: 'var(--sl-custom-app-padding-lg)',
                color: 'var(--streamlain-text-secondary-color)',
                textAlign: 'center' as const,
              },
            },
            emptyMessage
          )
    );
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      '☰ Article List'
    );
  },
};

function readWorkspaceId(
  runtimeContext?: ViewRuntimeContext
): string | undefined {
  const value = runtimeContext?.customApp?.['workspaceId'];
  return typeof value === 'string' && value.trim() !== ''
    ? value.trim()
    : undefined;
}
